import React, { useRef } from 'react';
import { SelectionBarProps } from '../../../types/DesignSystem/SelectionBar';
import Badge from '../Badge/Badge';
import IconButton from '../IconButton/IconButton';
import { Cross } from '../Iconography/Icons';
import { handleListNavKey } from '../listKeyboardNav';
import { useMergedRef } from '../useMergedRef';
import { MOTION } from '../motion';
import { withClassName } from '../cn';
import { useLiveRegionReady } from '../liveRegion';

/**
 * SelectionBar (§ 299) — see the type file for the contract and why it exists.
 *
 * Chrome is the raised surface at the overlay elevation (`ds-lg`, § 42: the bar floats
 * over the page, so it takes the tier Dialog and Toast take), the `srf` radius, and the
 * toast's entrance — it rises from the bottom because that is where it lives. The count
 * is `Badge selected sm`: the same pair a selected row wears, so the number and the rows
 * it counts read as one thing. `text-ink` on the root: a DS surface owes its content
 * slots an ink (§ 55).
 *
 * `sticky`, not `fixed`: a fixed bar needs a rung on the layer ladder and would compete
 * with the legacy modal root; a sticky one lives in the page's own flow, floats at the
 * viewport edge only while its natural place is below the fold, and is always under a
 * real overlay. `tw-z-40` is a LOCAL tier (§ 204): it must paint over a table's pinned
 * corner cell (`STICKY_Z.headerEdge`, 30) when the two overlap in a short viewport.
 */
const SelectionBar = React.forwardRef<HTMLDivElement, SelectionBarProps>(function SelectionBar({
  count,
  noun = 'selected',
  onClear,
  clearLabel = 'Clear selection',
  label = 'Selection actions',
  placement = 'sticky',
  children,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, forwardedRef) {
  const barRef = useRef<HTMLDivElement>(null);
  const setRef = useMergedRef<HTMLDivElement>(barRef, forwardedRef);
  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);
  /* § 396 — a bar that appears with the first selection brings its count region with it, so
     the first count waits ANNOUNCE_DELAY_MS to be heard (liveRegion.tsx); later counts are
     changes in a region that already exists. */
  const ready = useLiveRegionReady();
  const hiddenUntilReady = ready ? undefined : true;

  return (
    <div
      /* rest first — the Button.tsx rule. */
      {...rest}
      ref={setRef}
      role="toolbar"
      aria-label={label}
      /* The APG toolbar keyboard: arrows walk the actions, Home/End jump. Every action is
         a real button, so Enter/Space are the browser's; `listKeyboardNav` re-reads the
         buttons on each key, so an action that appears with the selection joins the walk. */
      onKeyDown={(event) =>
        handleListNavKey(event, {
          container: barRef.current,
          itemSelector: 'button:not([disabled])',
          orientation: 'horizontal',
        })
      }
      data-placement={placement}
      data-testid={dataTestId}
      className={withClassName(`${
        placement === 'sticky' ? 'tw-sticky tw-bottom-4 tw-z-40 tw-mx-auto tw-flex tw-w-max' : 'tw-inline-flex'
      } tw-max-w-full tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-surface-raised tw-border tw-border-edge tw-rounded-srf tw-shadow-ds-lg tw-text-ink tw-whitespace-nowrap ${MOTION.toast.enter}`, className)}
    >
      {/* The count. `role="status"`: a selection that grows or shrinks is announced,
          politely, without the toolbar itself becoming a live region. */}
      <span
        role="status"
        className="tw-inline-flex tw-items-center tw-gap-2 tw-pr-2 tw-border-r tw-border-edge"
        data-testid={testId('count')}
      >
        <Badge variant="selected" size="sm" aria-hidden={hiddenUntilReady}>{count}</Badge>
        <span className="tw-text-body-primary tw-text-ink" aria-hidden={hiddenUntilReady}>{noun}</span>
      </span>
      {children && <span className="tw-inline-flex tw-items-center tw-gap-1">{children}</span>}
      <IconButton variant="subtle" size="sm" aria-label={clearLabel} onClick={onClear} data-testid={testId('clear')}>
        <Cross size={16} aria-hidden="true" />
      </IconButton>
    </div>
  );
});

export default SelectionBar;
